import * as dominoCoreSDK from '@citrite/domino-core-sdk';
import { ResourceInfo } from '@citrite/domino-core-sdk';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import {
	ExecuteTypeSpecParams,
	GetResourcesParams,
	TypeSpecExecutionResult,
} from './types';
import { apiProxy, getResolvedResourceName } from './utils';

export interface DominoClient {
	resources: {
		listResources(params: GetResourcesParams): Promise<ResourceInfo[]>;
		execute(params: ExecuteTypeSpecParams): Promise<TypeSpecExecutionResult>;
	};
}

class DominoClientWrapper implements DominoClient {
	private initializedClient?: DominoClient;

	public _initialize(dominoClient: DominoClient) {
		this.initializedClient = dominoClient;
	}

	public resources = {
		listResources: (params: GetResourcesParams): Promise<ResourceInfo[]> => {
			return this.initializedClient!.resources.listResources(params);
		},
		execute: (params: ExecuteTypeSpecParams): Promise<TypeSpecExecutionResult> => {
			return this.initializedClient!.resources.execute(params);
		},
	};
}

export interface DominoClientConfiguration {
	baseUrl: string;
	/**
	 * The token acquisition callback is called before a request if there is no cached token.
	 * It's additionally called whenever the RightSignature API returns a 401 authentication
	 * challenge response, in order to refresh an expired token. Avoid providing a cached token
	 * to this callback; caching is handled in the SDK.
	 */
	acquireAccessToken?: () => Promise<string>;
}

function createDominoClient(configuration: DominoClientConfiguration): DominoClient {
	const sharedConfig = new dominoCoreSDK.Configuration({
		basePath: configuration.baseUrl,
		baseOptions: {
			timeout: 60000,
		},
	});
	const resourcesApi = new dominoCoreSDK.ResourcesApi(sharedConfig);

	return {
		resources: {
			listResources({ type, category }: GetResourcesParams) {
				return apiProxy<ResourceInfo[]>(
					headers => resourcesApi.listResources(type, category, { headers }) as any,
					'listResources'
				);
			},
			execute({ type, specName, specArgs, typeArgs }: ExecuteTypeSpecParams) {
				const {
					namespace,
					package: _package,
					name,
					version,
				} = getResolvedResourceName(type);

				return apiProxy<any>(
					headers =>
						resourcesApi.executeResourceMethod(
							namespace,
							_package,
							name,
							{
								specName,
								specArgs,
								typeArgs: typeArgs ?? {},
							},
							version,
							{ headers }
						) as any,
					'executeTypeSpec'
				);
			},
		},
	};
}

export const dominoApiClient: DominoClient = new DominoClientWrapper();

export function configureDominoApiClient(_api: PiletApi) {
	let suffix = '';
	if (!!window.origin.match(/.+\.sharefiletest\..+$/)) {
		suffix = '-dev-master';
	}

	(dominoApiClient as DominoClientWrapper)._initialize(
		createDominoClient({
			baseUrl: `${window.origin}/io/workflows/workflows${suffix}`,
		})
	);
}
